import React, {
  useRef,
  useState,
  useEffect,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from "react";

import { Box, MenuItem } from "@mui/material";
import JoditEditor, { IJoditEditorProps } from "jodit-react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { useQuery, useMutation } from "@tanstack/react-query";

import { useAppState } from "../../../../../context/StateContext";
import useYupValidation from "../../../../../hooks/useYupValidation";
import { templateTags } from "../../../../../constants";
import { handleInsertTemplateTag, addTagsToString } from "../../../../../util/joditEditor";
import { convertHtmlToPlainText } from "../../../../../util/notificationTemplatePreview";
import { Input, Select } from "../../../../Form";
import { FormError } from "../../../../Form/FormError";
import { NotificationToast } from "../../../Toast/NotificationToast";
import SkeletonComponent from "../../../Skeleton";
import CommunicationCentreService from "../../../../../services/communication.centre.service";
import { NotificationTemplate } from "../../../../../models/notification.template";
import { Status, UsedInEmailTemplates } from "../../../../../util/enums";
import { EntityContext } from "../../index";
import { getTypeFromEntityContext } from "../../utils";
import {
  SMSEditorContainer,
  SectionTitle,
  FormRow,
  EditorContainer,
  CharacterCounter,
  CharacterCounterText,
  CharacterCounterHint,
} from "./styles";

interface SMSEditorProps {
  entityContext: EntityContext;
  usedIn?: UsedInEmailTemplates;
  onSend: () => void;
  onCancel: () => void;
}

export interface SMSEditorHandle {
  sendSMS: (scheduledDate?: string) => void;
  isSending: boolean;
}

const MAX_CHARACTERS = 168;

const SMSEditor = forwardRef<SMSEditorHandle, SMSEditorProps>(
  ({ entityContext, usedIn = UsedInEmailTemplates.ORDERS, onSend, onCancel }, ref) => {
    const { t: translate } = useTranslation();
    const { currentUser } = useAppState();
    const textEditorRef = useRef(null);

    const entityId = entityContext.entityId;
    const entity = entityContext.entity;
    const customer = entityContext.customer;

    const [selectedSMSTemplate, setSelectedSMSTemplate] = useState<NotificationTemplate>(
      {} as NotificationTemplate
    );
    const [smsContent, setSmsContent] = useState<string>("");
    const [smsSubject, setSmsSubject] = useState<string>("");
    const [smsFrom, setSmsFrom] = useState<string>("");
    const [smsTo, setSmsTo] = useState<string>("");
    const [errors, setErrors] = useState<yup.ValidationError[]>([]);

    const { fieldHasError, getErrorByField } = useYupValidation(errors);

    const { data: smsTemplatesResponse, isLoading: isSMSTemplatesLoading } = useQuery(
      ["communication-centre.templates.sms", usedIn, currentUser?.companyId],
      () =>
        CommunicationCentreService.getAll({
          status: Status.ACTIVE,
          companyId: currentUser?.companyId,
          sortBy: JSON.stringify([["name", "asc"]]),
        }),
      {
        enabled: !!currentUser?.companyId,
        refetchOnWindowFocus: false,
        select: (data) => ({
          ...data,
          data:
            data?.data?.filter((template: NotificationTemplate) =>
              template.notificationTypes?.includes("sms")
            ) || [],
        }),
      }
    );

    const smsTemplates = smsTemplatesResponse?.data || [];

    const { mutate: sendTemplate, isLoading: isSending } = useMutation(
      ["send.notification.template"],
      async (payload: any) => {
        return await CommunicationCentreService.sendTemplate(payload);
      },
      {
        onSuccess: () => {
          NotificationToast.success(
            translate("communicationSentSuccessfully") || "Communication sent successfully"
          );
          onSend();
        },
        onError: (error: any) => {
          NotificationToast.error(
            error?.response?.data?.message ||
              translate("anErrorOccurredTryAgain") ||
              "An error occurred"
          );
        },
      }
    );

    useEffect(() => {
      if (currentUser?.company?.name) {
        setSmsFrom(currentUser.company.name);
      }
      if (customer?.mobile || customer?.phone) {
        setSmsTo(customer.mobile || customer.phone || "");
      }
    }, [currentUser, customer]);

    useEffect(() => {
      if (selectedSMSTemplate.content) {
        setSmsContent(addTagsToString(selectedSMSTemplate.content));
      }
    }, [selectedSMSTemplate.id]);

    const characterCount = useMemo(() => {
      const plainText = convertHtmlToPlainText(smsContent);
      return plainText.length;
    }, [smsContent]);

    const config: IJoditEditorProps["config"] = {
      height: "326px",
      toolbarButtonSize: "middle",
      removeButtons: ["fullsize"],
      buttons: ["bold", "italic", "underline", "fontsize", "link", "undo", "redo"],
      controls: {
        customer: {
          name: (translate("customer") as string) || "Customer",
          exec: handleInsertTemplateTag,
          list: templateTags.customer,
        },
        quote: {
          name: (translate("quote") as string) || "Quote",
          exec: handleInsertTemplateTag,
          list: templateTags.quote,
        },
        order: {
          name: (translate("order") as string) || "Order",
          exec: handleInsertTemplateTag,
          list: templateTags.order,
        },
        company: {
          name: (translate("company") as string) || "Company",
          exec: handleInsertTemplateTag,
          list: templateTags.company,
        },
      },
      uploader: {
        insertImageAsBase64URI: true,
      },
      extraButtons: ["quote", "order", "shipment", "customer"],
    };

    const schema = yup.object().shape({
      from: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
      to: yup
        .string()
        .trim()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired"))
        .test("character-limit", `Maximum ${MAX_CHARACTERS} characters allowed`, (value) => {
          if (!value) return true;
          const text = value
            .replace(/<[^>]*>/g, "")
            .replace(/<span class="joditTag">.*?<\/span>/g, "")
            .replace(/&nbsp;/g, " ")
            .trim();
          return text.length <= MAX_CHARACTERS;
        }),
      content: yup
        .string()
        .required(translate("thisFieldIsRequired"))
        .typeError(translate("thisFieldIsRequired")),
    });

    const sendSMSHandler = (scheduledDate?: string) => {
      if (!onValidation()) {
        return;
      }

      if (!entityId) {
        NotificationToast.error(translate("anErrorOccurredTryAgain") || "An error occurred");
        return;
      }

      // Start with the selected template (or empty template if none selected)
      const templateData: NotificationTemplate = selectedSMSTemplate.id
        ? { ...selectedSMSTemplate }
        : ({} as NotificationTemplate);

      // Overlay user edits - preserve all template fields, only update edited ones
      const updatedTemplate: NotificationTemplate = {
        ...templateData,
        content: convertHtmlToPlainText(smsContent || templateData.content || ""),
        subject: smsSubject || templateData.subject || "",
        fromName: smsFrom || templateData.fromName || currentUser?.company?.name || "",
        fromEmail: smsFrom || templateData.fromEmail || currentUser?.company?.name || "",
      };

      const payload = {
        ...updatedTemplate,
        to: smsTo || "",
        notificationType: "sms" as const,
        notificationItem: {
          id: entityId,
          type: getTypeFromEntityContext(entityContext),
        },
      };

      sendTemplate(payload);
    };

    const onValidation = () => {
      try {
        schema.validateSync(
          {
            from: smsFrom,
            to: smsTo,
            content: smsContent,
          },
          {
            abortEarly: false,
          }
        );
        setErrors([]);
        return true;
      } catch (error) {
        if (error instanceof yup.ValidationError) {
          setErrors(error.inner);
        }
        return false;
      }
    };

    useImperativeHandle(ref, () => ({
      sendSMS: (scheduledDate?: string) => sendSMSHandler(scheduledDate),
      isSending,
    }));

    return (
      <SMSEditorContainer>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", padding: "16px" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <SectionTitle>{translate("smsDetails") || "SMS Details"}</SectionTitle>

            <FormRow>
              <Box sx={{ flex: 1 }}>
                <SkeletonComponent isLoading={isSMSTemplatesLoading} isInput>
                  <Select
                    name="template"
                    label={translate("template") || "Template"}
                    value={selectedSMSTemplate?.id || ""}
                    onChange={(event) => {
                      if (Number(event.target.value)) {
                        const smsTemplate = smsTemplates?.find(
                          (template: NotificationTemplate) =>
                            template.id === Number(event.target.value)
                        );
                        if (smsTemplate) {
                          setSelectedSMSTemplate(smsTemplate);
                          setSmsContent(addTagsToString(smsTemplate.content || ""));
                        }
                      } else {
                        setSelectedSMSTemplate({} as NotificationTemplate);
                      }
                    }}
                    placeholder={translate("selectATemplate") || "Select a template"}
                    fullWidth
                  >
                    {smsTemplates?.map((template) => (
                      <MenuItem key={template.name} value={template.id}>
                        {template.name}
                      </MenuItem>
                    ))}
                  </Select>
                </SkeletonComponent>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Input
                  label={translate("from") || "From"}
                  value={smsFrom}
                  onChange={(event) => setSmsFrom(event.target.value)}
                  disabled
                  fullWidth
                  required
                  error={fieldHasError("from")}
                  errorMessage={getErrorByField("from")}
                />
              </Box>
            </FormRow>

            <FormRow>
              <Box sx={{ flex: 1 }}>
                <Input
                  label={translate("to") || "To"}
                  value={smsTo}
                  onChange={(event) => setSmsTo(event.target.value)}
                  placeholder={"+00 0000000000"}
                  fullWidth
                  required
                  error={fieldHasError("to")}
                  errorMessage={getErrorByField("to")}
                />
              </Box>
            </FormRow>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <SectionTitle>
              {translate("smsContent") || "SMS Content"}
              <span style={{ color: "#DC3545" }}>{" *"}</span>
            </SectionTitle>
            <FormError isError={!!getErrorByField("content")}>{getErrorByField("content")}</FormError>
            <EditorContainer>
              <JoditEditor
                ref={textEditorRef}
                value={smsContent}
                config={config}
                onBlur={(newContent) => {
                  setSmsContent(newContent);
                }}
              />
              <CharacterCounter>
                <CharacterCounterText>
                  {`${characterCount} / ${MAX_CHARACTERS} ${
                    translate("characters") || "characters"
                  }`}
                </CharacterCounterText>
                <CharacterCounterHint>
                  {translate("smsCharacterLimitHint") ||
                    "SMS messages over 168 characters will be split into multiple messages."}
                </CharacterCounterHint>
              </CharacterCounter>
            </EditorContainer>
          </Box>

        </Box>
      </SMSEditorContainer>
    );
  }
);

SMSEditor.displayName = "SMSEditor";

export default SMSEditor;
